import { useMemo, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  Barcode,
  FileSpreadsheet,
  LibraryBig,
  Package,
  Plus,
  Search,
  Tags,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Card,
  EmptyState,
  Money,
  PageHeader,
  StatusBadge,
} from "../components/ui";
import { useAuth } from "../lib/auth";
import { db } from "../lib/db";

type Filter = "ALL" | "IN_STOCK" | "OUT" | "EXPIRY";

export function ProductCatalogPage() {
  const auth = useAuth();
  const role = auth.session?.user.role ?? "OWNER";
  const canManageProducts = ["OWNER", "MANAGER"].includes(role);
  const products = useLiveQuery(
    () => db.products.orderBy("name").toArray(),
    [],
    [],
  );

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("ALL");
  const [category, setCategory] = useState("");

  const categories = useMemo(
    () =>
      [...new Set(products.map((product) => product.category).filter((value): value is string => Boolean(value)))]
        .sort((a, b) => a.localeCompare(b, "vi")),
    [products],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("vi");

    return products.filter((product) => {
      if (
        needle &&
        !product.name.toLocaleLowerCase("vi").includes(needle) &&
        !product.sku.toLocaleLowerCase("vi").includes(needle) &&
        !product.barcode?.toLocaleLowerCase("vi").includes(needle) &&
        !product.businessVertical?.toLocaleLowerCase("vi").includes(needle) &&
        !product.category?.toLocaleLowerCase("vi").includes(needle) &&
        !product.subcategory?.toLocaleLowerCase("vi").includes(needle)
      ) {
        return false;
      }

      if (category && product.category !== category) return false;

      if (filter === "IN_STOCK" && product.stockQty <= 0) return false;
      if (filter === "OUT" && product.stockQty > 0) return false;
      if (filter === "EXPIRY" && !product.trackExpiry) return false;

      return true;
    });
  }, [products, query, filter, category]);

  const totalSku = products.length;
  const inStock = products.filter((product) => product.stockQty > 0).length;
  const outOfStock = totalSku - inStock;

  return (
    <div className="page-stack">
      <PageHeader
        title="Danh mục sản phẩm"
        description="Product Master / SKU: khai báo mặt hàng độc lập với số lượng tồn kho thực tế"
        action={
          canManageProducts ? (
            <div className="header-actions">
              <Link className="btn btn-secondary" to="/products/library">
                <LibraryBig size={18} /> Thư viện 5.000 SKU
              </Link>
              <Link className="btn btn-secondary" to="/products/new?mode=import">
                <FileSpreadsheet size={18} /> Import danh mục
              </Link>
              <Link className="btn btn-primary" to="/products/new">
                <Plus size={18} /> Thêm sản phẩm
              </Link>
            </div>
          ) : undefined
        }
      />

      <div className="dashboard-grid">
        <Card>
          <small>Tổng SKU đang quản lý</small>
          <strong>{totalSku}</strong>
        </Card>
        <Card>
          <small>SKU đang có hàng</small>
          <strong>{inStock}</strong>
        </Card>
        <Card>
          <small>SKU tồn kho = 0</small>
          <strong>{outOfStock}</strong>
        </Card>
      </div>

      <div className="search-box">
        <Search size={20} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Tìm tên sản phẩm, SKU hoặc mã vạch"
        />
      </div>

      {categories.length > 0 ? (
        <label>
          Danh mục hàng hóa
          <select value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="">Tất cả danh mục</option>
            {categories.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>
      ) : null}

      <div className="filter-chips">
        {[
          ["ALL", "Tất cả SKU"],
          ["IN_STOCK", "Đang có hàng"],
          ["OUT", "Tồn = 0"],
          ["EXPIRY", "Theo dõi HSD"],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={filter === value ? "active" : ""}
            onClick={() => setFilter(value as Filter)}
          >
            {label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title={products.length === 0 ? "Chưa có SKU nào" : "Không có SKU phù hợp"}
          description={
            products.length === 0
              ? "Tạo Product Master trước. Số lượng tồn sẽ chỉ phát sinh khi thực hiện Nhập hàng hoặc Điều chỉnh tồn."
              : "Thử đổi bộ lọc hoặc từ khóa tìm kiếm."
          }
          action={
            products.length === 0 && canManageProducts ? (
              <Link className="btn btn-primary" to="/products/new">
                <Plus size={18} /> Tạo SKU đầu tiên
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="product-list">
          {filtered.map((product) => (
            <Card
              key={product.id}
              className="inventory-row inventory-row-rich"
            >
              <div className="product-avatar">
                {product.name.slice(0, 1).toUpperCase()}
              </div>

              <div className="product-main">
                <strong>{product.name}</strong>

                <div className="product-meta">
                  <span className="inline-flex items-center gap-1">
                    <Tags size={14} />
                    SKU <b>{product.sku}</b>
                  </span>
                  {product.barcode ? (
                    <span className="inline-flex items-center gap-1">
                      <Barcode size={14} />
                      {product.barcode}
                    </span>
                  ) : null}
                </div>

                <div className="product-meta">
                  <span>
                    ĐVT <b>{product.baseUnit}</b>
                  </span>
                  <Money value={product.salePrice} />
                  <span>
                    Tồn tham chiếu <b>{product.stockQty} {product.baseUnit}</b>
                  </span>
                </div>

                <div className="inventory-badges">
                  {product.businessVertical ? <StatusBadge tone="info">{product.businessVertical}</StatusBadge> : null}
                  {product.category ? <StatusBadge tone="neutral">{product.category}</StatusBadge> : null}
                  {product.subcategory ? <StatusBadge tone="neutral">{product.subcategory}</StatusBadge> : null}
                  <StatusBadge tone={product.stockQty > 0 ? "success" : "neutral"}>
                    {product.stockQty > 0 ? "Đang có hàng" : "Tồn = 0"}
                  </StatusBadge>
                  {product.trackExpiry ? (
                    <StatusBadge tone="warning">Theo dõi HSD/Lô</StatusBadge>
                  ) : null}
                  {product.units.length > 0 ? (
                    <StatusBadge tone="info">
                      {product.units.length} quy cách
                    </StatusBadge>
                  ) : null}
                </div>
              </div>

              <Link className="btn btn-secondary compact" to="/inventory/receive">
                <Package size={17} /> Nhập hàng
              </Link>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
